
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useLanguage } from '@/context/LanguageContext';
import { Brain, Heart, Users, Baby, Zap, Shield, Clock, Star } from 'lucide-react';
import { Link } from 'react-router-dom';
import MasterSEOController from '@/components/seo/MasterSEOController';

const Specialties = () => {
  const { t, getLocalizedPath, language } = useLanguage();
  
  const specialtyCategories = [
    {
      title: t('individualTherapy'),
      icon: Brain,
      color: 'bg-blue-500',
      specialties: [
        { name: t('anxietyDisorders'), sessions: '15,420', description: t('anxietyDescription') },
        { name: t('depression'), sessions: '18,650', description: t('depressionDescription') },
        { name: t('traumaPTSD'), sessions: '12,380', description: t('traumaDescription') },
        { name: t('addictionRecovery'), sessions: '9,760', description: t('addictionDescription') },
        { name: t('eatingDisorders'), sessions: '7,290', description: t('eatingDescription') },
        { name: t('bipolarDisorder'), sessions: '5,840', description: t('bipolarDescription') }
      ]
    },
    {
      title: t('relationshipTherapy'),
      icon: Heart,
      color: 'bg-red-500',
      specialties: [
        { name: t('couplesTherapy'), sessions: '11,250', description: t('couplesDescription') },
        { name: t('marriageCounseling'), sessions: '9,870', description: t('marriageDescription') },
        { name: t('familyTherapy'), sessions: '8,450', description: t('familyDescription') },
        { name: t('divorceCounseling'), sessions: '6,720', description: t('divorceDescription') },
        { name: t('premaritalCounseling'), sessions: '4,580', description: t('premaritalDescription') }
      ]
    },
    {
      title: t('groupTherapy'),
      icon: Users,
      color: 'bg-green-500',
      specialties: [
        { name: t('supportGroups'), sessions: '8,940', description: t('supportGroupsDescription') },
        { name: t('groupCBT'), sessions: '6,780', description: t('groupCBTDescription') },
        { name: t('griefSupport'), sessions: '5,620', description: t('griefDescription') },
        { name: t('substanceAbuseGroups'), sessions: '7,340', description: t('substanceDescription') }
      ]
    },
    {
      title: t('childAdolescentTherapy'),
      icon: Baby,
      color: 'bg-purple-500',
      specialties: [
        { name: t('childTherapy'), sessions: '10,650', description: t('childTherapyDescription') },
        { name: t('adolescentCounseling'), sessions: '9,120', description: t('adolescentDescription') },
        { name: t('behavioralIssues'), sessions: '7,890', description: t('behavioralDescription') },
        { name: t('autismSupport'), sessions: '6,450', description: t('autismDescription') },
        { name: t('learningDisabilities'), sessions: '5,780', description: t('learningDescription') }
      ]
    }
  ];

  const therapeuticApproaches = [
    {
      name: t('cognitiveBehavioralTherapy'),
      abbreviation: 'CBT',
      description: t('cbtDescription'),
      effectiveness: '92%',
      sessions: '45,680'
    },
    {
      name: t('dialecticalBehaviorTherapy'),
      abbreviation: 'DBT',
      description: t('dbtDescription'),
      effectiveness: '89%',
      sessions: '23,450'
    },
    {
      name: t('emdr'),
      abbreviation: 'EMDR',
      description: t('emdrDescription'),
      effectiveness: '87%',
      sessions: '18,920'
    },
    {
      name: t('psychodynamicTherapy'),
      abbreviation: 'PDT',
      description: t('psychodynamicDescription'),
      effectiveness: '85%',
      sessions: '32,180'
    },
    {
      name: t('humanisticTherapy'),
      abbreviation: 'HT',
      description: t('humanisticDescription'),
      effectiveness: '83%',
      sessions: '28,760'
    },
    {
      name: t('briefPsychotherapy'),
      abbreviation: 'BP',
      description: t('briefPsychotherapyDescription'),
      effectiveness: '94%',
      sessions: '52,340'
    }
  ];

  return (
    <>
      {/* Master SEO Controller */}
      <MasterSEOController 
        pageType="specialty"
        currentLanguage={language}
        customMetaTags={{
          title: "Therapy Specialties & Treatment Approaches",
          description: "Explore 150+ therapy specialties and evidence-based treatment approaches. Find specialized therapists for anxiety, depression, trauma, couples therapy, family counseling, and more.",
          keywords: ['therapy specialties', 'mental health treatments', 'CBT', 'EMDR', 'DBT', 'psychotherapy approaches', 'family therapy', 'couples counseling', 'trauma therapy', 'brief psychotherapy']
        }}
        enabledFeatures={{
          multilingual: true,
          faqSchema: true,
          trustSignals: true
        }}
        ratings={{
          averageRating: 4.8,
          reviewCount: 3241
        }}
      />
      
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-brand-blue-50 to-white py-16 md:py-24">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-6xl font-bold text-brand-gray-900 mb-6">
              {t('therapySpecialties')}
            </h1>
            <p className="text-xl text-brand-gray-600 mb-8 leading-relaxed">
              {t('findSpecializedCareForYourNeeds')}
            </p>
            <div className="flex flex-wrap justify-center gap-2">
              <Badge variant="secondary">{t('150Plus')} {t('specialties')}</Badge>
              <Badge variant="secondary">{t('certifiedProfessionals')}</Badge>
              <Badge variant="secondary">{t('evidenceBasedTreatments')}</Badge>
            </div>
          </div>
        </div>
      </div>

      {/* Specialty Categories */}
      <div className="py-16 md:py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-center text-brand-gray-900 mb-12">
              {t('specialtyCategories')}
            </h2>
            
            <div className="space-y-12">
              {specialtyCategories.map((category, categoryIndex) => (
                <div key={categoryIndex}>
                  <div className="flex items-center gap-4 mb-8">
                    <div className={`w-12 h-12 ${category.color} text-white rounded-full flex items-center justify-center`}>
                      <category.icon className="w-6 h-6" />
                    </div>
                    <h3 className="text-2xl font-bold text-brand-gray-900">{category.title}</h3>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {category.specialties.map((specialty, index) => (
                      <Card key={index} className="hover:shadow-lg transition-shadow">
                        <CardHeader>
                          <CardTitle className="text-lg text-brand-gray-900">
                            {specialty.name}
                          </CardTitle>
                          <div className="flex items-center gap-2">
                            <Star className="w-4 h-4 text-yellow-500" />
                            <span className="text-sm text-brand-gray-600">
                              {specialty.sessions} {t('completedSessions')}
                            </span>
                          </div>
                        </CardHeader>
                        <CardContent>
                          <p className="text-brand-gray-600 text-sm leading-relaxed mb-4">
                            {specialty.description}
                          </p>
                          <Link to={getLocalizedPath('/find')}>
                            <Button size="sm" variant="outline" className="w-full">
                              {t('findSpecialist')}
                            </Button>
                          </Link>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Therapeutic Approaches */}
      <div className="bg-brand-gray-50 py-16 md:py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-center text-brand-gray-900 mb-12">
              {t('therapeuticApproaches')}
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {therapeuticApproaches.map((approach, index) => (
                <Card key={index} className="hover:shadow-lg transition-shadow">
                  <CardHeader>
                    <div className="flex items-center justify-between mb-2">
                      <CardTitle className="text-lg text-brand-gray-900">
                        {approach.name}
                      </CardTitle>
                      <Badge variant="secondary">{approach.abbreviation}</Badge>
                    </div>
                    <div className="flex items-center gap-4 text-sm text-brand-gray-600">
                      <div className="flex items-center gap-1">
                        <Zap className="w-4 h-4 text-green-500" />
                        <span>{approach.effectiveness} {t('effective')}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Clock className="w-4 h-4 text-blue-500" />
                        <span>{approach.sessions}</span>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-brand-gray-600 text-sm leading-relaxed mb-4">
                      {approach.description}
                    </p>
                    <Link to={getLocalizedPath('/find')}>
                      <Button size="sm" variant="outline" className="w-full">
                        {t('findTherapist')}
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Dr. José Luis Marín Section */}
      <div className="py-16 md:py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-brand-gray-900 mb-6">
              {t('briefPsychotherapyExcellence')}
            </h2>
            <p className="text-xl text-brand-gray-600 mb-8 leading-relaxed">
              {t('allOurTherapistsTrained')} <strong>{t('drJoseLuisMarin')}</strong>, {t('worldRenownedExpert')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to={getLocalizedPath('/jose-luis-marin')}>
                <Button size="lg" variant="outline">
                  {t('learnAboutDrMarin')}
                </Button>
              </Link>
              <Link to={getLocalizedPath('/find')}>
                <Button size="lg">
                  {t('findCertifiedTherapist')}
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Specialties;
